import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Root-level error boundary. Prevents a single page-level error from
 * blanking the whole site — keeps Navbar/Footer mounted and shows a
 * recoverable fallback instead.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface via the browser console; production telemetry can hook here later.
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught:', error, info);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="container-page flex flex-col items-center justify-center gap-4 py-24 text-center text-brand-purple"
      >
        <h1 className="text-2xl md:text-3xl font-semibold">
          Algo deu errado.
        </h1>
        <p className="text-sm md:text-base text-brand-purple/70 max-w-md">
          Tivemos um problema ao carregar esta página. Tente recarregar — se
          o erro persistir, fale com a gente pelo WhatsApp.
        </p>
        <button
          type="button"
          onClick={this.handleReset}
          className="rounded-full bg-brand-yellow px-6 py-2 text-sm font-semibold text-brand-purple hover:bg-brand-yellow/90 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-purple"
        >
          Tentar novamente
        </button>
      </div>
    );
  }
}
